/**
 * Water Quality Input Form + Suitability Display
 *
 * Matches Dalhart report Table 5 — accepts lab analytical values,
 * submits to Calculator 9, displays suitability ratings with
 * colored badges and warning flags.
 */
import React, { useState } from 'react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  BeakerIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { phreaticApi } from '../../api/phreatic';

interface WQInput {
  well_id: string;
  tds_mg_l: string;
  hardness_mg_l: string;
  nitrate_mg_l: string;
  sulfate_mg_l: string;
  arsenic_mg_l: string;
  fluoride_mg_l: string;
  boron_mg_l: string;
  sar: string;
  ph: string;
  chloride_mg_l: string;
  iron_mg_l: string;
}

interface WQResult {
  well_id: string | null;
  tds_mg_l: number | null;
  hardness_mg_l: number | null;
  nitrate_mg_l: number | null;
  sulfate_mg_l: number | null;
  arsenic_mg_l: number | null;
  fluoride_mg_l: number | null;
  boron_mg_l: number | null;
  sar: number | null;
  ph: number | null;
  chloride_mg_l: number | null;
  iron_mg_l: number | null;
  suitability: {
    livestock: string;
    irrigation: string;
    drinking: string;
    cooling: string;
  };
  flags: string[];
}

const EMPTY_INPUT: WQInput = {
  well_id: '', tds_mg_l: '', hardness_mg_l: '', nitrate_mg_l: '',
  sulfate_mg_l: '', arsenic_mg_l: '', fluoride_mg_l: '', boron_mg_l: '',
  sar: '', ph: '', chloride_mg_l: '', iron_mg_l: '',
};

// Dalhart report values for pre-fill
const DALHART_DEFAULTS: WQInput = {
  well_id: 'Well #2', tds_mg_l: '367', hardness_mg_l: '240', nitrate_mg_l: '2.2',
  sulfate_mg_l: '48', arsenic_mg_l: '', fluoride_mg_l: '', boron_mg_l: '',
  sar: '', ph: '8.3', chloride_mg_l: '19', iron_mg_l: '0.05',
};

const FIELDS: { key: keyof WQInput; label: string; unit: string; placeholder: string }[] = [
  { key: 'tds_mg_l', label: 'TDS', unit: 'mg/L', placeholder: '367' },
  { key: 'hardness_mg_l', label: 'Hardness (CaCO3)', unit: 'mg/L', placeholder: '240' },
  { key: 'nitrate_mg_l', label: 'Nitrate (NO3+NO2-N)', unit: 'mg/L', placeholder: '2.2' },
  { key: 'sulfate_mg_l', label: 'Sulfate (SO4)', unit: 'mg/L', placeholder: '48' },
  { key: 'chloride_mg_l', label: 'Chloride (Cl)', unit: 'mg/L', placeholder: '19' },
  { key: 'ph', label: 'pH', unit: '', placeholder: '8.3' },
  { key: 'arsenic_mg_l', label: 'Arsenic (As)', unit: 'mg/L', placeholder: '0.01' },
  { key: 'fluoride_mg_l', label: 'Fluoride (F)', unit: 'mg/L', placeholder: '1.0' },
  { key: 'boron_mg_l', label: 'Boron (B)', unit: 'mg/L', placeholder: '0.5' },
  { key: 'sar', label: 'SAR', unit: '', placeholder: '3.0' },
  { key: 'iron_mg_l', label: 'Iron (Fe)', unit: 'mg/L', placeholder: '0.05' },
];

function suitabilityColor(rating: string): string {
  switch (rating) {
    case 'excellent': return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40';
    case 'good': return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40';
    case 'marginal': return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40';
    case 'unsuitable': return 'bg-red-500/20 text-red-300 border-red-500/40';
    case 'meets_mcl': return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40';
    case 'exceeds_mcl': return 'bg-red-500/20 text-red-300 border-red-500/40';
    case 'suitable': return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40';
    case 'treatment_needed': return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40';
    default: return 'bg-mono-800 text-mono-400 border-mono-700';
  }
}

export function WaterQualityPanel({ siteId }: { siteId: string }) {
  const [input, setInput] = useState<WQInput>(EMPTY_INPUT);
  const [results, setResults] = useState<WQResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateField = (key: keyof WQInput, value: string) => {
    setInput(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const wellData: any = { well_id: input.well_id || null };
      for (const f of FIELDS) {
        const val = input[f.key];
        wellData[f.key] = val !== '' ? parseFloat(val) : null;
      }
      const resp = await phreaticApi.calcWaterQuality({
        site_id: siteId,
        wells: [wellData],
      });
      setResults(resp.wells);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to evaluate water quality');
    } finally {
      setLoading(false);
    }
  };

  const loadDefaults = () => setInput(DALHART_DEFAULTS);

  return (
    <div className="space-y-3">
      {/* Input form */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider">
            <BeakerIcon className="w-4 h-4 inline mr-1" />
            Lab Analytical Input
          </h3>
          <div className="flex gap-2">
            <button onClick={loadDefaults}
              className="px-2 py-1 text-[10px] rounded bg-mono-800 text-mono-400 border border-mono-700 hover:text-mono-300">
              Load Dalhart Defaults
            </button>
            <button onClick={() => setInput(EMPTY_INPUT)}
              className="px-2 py-1 text-[10px] rounded bg-mono-800 text-mono-400 border border-mono-700 hover:text-mono-300">
              Clear
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div>
            <label className="text-[10px] text-mono-500 uppercase tracking-wider">Well ID</label>
            <input type="text" value={input.well_id} onChange={e => updateField('well_id', e.target.value)}
              placeholder="Well #2"
              className="w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none" />
          </div>
          {FIELDS.map(f => (
            <div key={f.key}>
              <label className="text-[10px] text-mono-500 uppercase tracking-wider">
                {f.label} {f.unit && <span className="text-mono-600">({f.unit})</span>}
              </label>
              <input type="number" step="any" value={input[f.key]}
                onChange={e => updateField(f.key, e.target.value)}
                placeholder={f.placeholder}
                className="w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none" />
            </div>
          ))}
        </div>

        <button onClick={handleSubmit} disabled={loading}
          className="flex items-center gap-1 px-4 py-1.5 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50">
          {loading ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <BeakerIcon className="w-3.5 h-3.5" />}
          {loading ? 'Evaluating...' : 'Evaluate Suitability'}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-red-400 text-xs">{error}</div>
      )}

      {/* Results */}
      {results && results.map((r, idx) => (
        <div key={idx} className="bg-mono-900 border border-mono-800 rounded-lg p-4 space-y-4">
          <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider">
            Suitability Results {r.well_id && `— ${r.well_id}`}
          </h3>

          {/* Suitability badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Livestock', value: r.suitability.livestock },
              { label: 'Irrigation', value: r.suitability.irrigation },
              { label: 'Drinking Water', value: r.suitability.drinking },
              { label: 'Cooling Systems', value: r.suitability.cooling },
            ].map(s => (
              <div key={s.label} className={`rounded-lg border p-3 text-center ${suitabilityColor(s.value)}`}>
                <div className="text-[10px] uppercase tracking-wider opacity-70">{s.label}</div>
                <div className="text-sm font-bold mt-0.5">{s.value.replace('_', ' ').toUpperCase()}</div>
              </div>
            ))}
          </div>

          {/* Parameter values table */}
          <div>
            <h4 className="text-xs font-semibold text-mono-300 mb-2">Analytical Values</h4>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
              {[
                { label: 'TDS', val: r.tds_mg_l, unit: 'mg/L' },
                { label: 'Hardness', val: r.hardness_mg_l, unit: 'mg/L' },
                { label: 'Nitrate', val: r.nitrate_mg_l, unit: 'mg/L' },
                { label: 'Sulfate', val: r.sulfate_mg_l, unit: 'mg/L' },
                { label: 'Chloride', val: r.chloride_mg_l, unit: 'mg/L' },
                { label: 'pH', val: r.ph, unit: '' },
                { label: 'Arsenic', val: r.arsenic_mg_l, unit: 'mg/L' },
                { label: 'Fluoride', val: r.fluoride_mg_l, unit: 'mg/L' },
                { label: 'Boron', val: r.boron_mg_l, unit: 'mg/L' },
                { label: 'SAR', val: r.sar, unit: '' },
                { label: 'Iron', val: r.iron_mg_l, unit: 'mg/L' },
              ].filter(p => p.val != null).map(p => (
                <div key={p.label} className="bg-mono-800 rounded p-1.5">
                  <div className="text-[9px] text-mono-500">{p.label}</div>
                  <div className="text-mono-200 font-medium">{p.val} {p.unit}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Flags / warnings */}
          {r.flags.length > 0 && (
            <div className="space-y-1">
              <h4 className="text-xs font-semibold text-mono-300">Flags & Warnings</h4>
              {r.flags.map((flag, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span className="text-cyan-300">{flag}</span>
                </div>
              ))}
            </div>
          )}
          {r.flags.length === 0 && (
            <div className="flex items-center gap-2 text-xs text-cyan-400">
              <CheckCircleIcon className="w-4 h-4" />
              No flags — all parameters within acceptable ranges
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
